import { Injectable, signal, Signal } from '@angular/core'
import {
    EmployeeLabourCodeInterface,
    EmployeesInterface,
    EmployeeTypesInterface
} from '@isl-features/employees/interfaces/employees.interface'
import { LeaveTypesInterface } from '@isl-features/employees/interfaces/leaves.interface'
import { SkillsInterface } from '@isl-features/employees/interfaces/skills.interface'
import { ShipTypesInterface } from '@isl-features/ships/interfaces/ship-types.interface'
import { ShipsInterface } from '@isl-features/ships/interfaces/ships.interface'
import { LabourSheetCounterInterface, ReportPeriodInterface } from '@isl-shared/interfaces/app.interface'

@Injectable({
    providedIn: 'root'
})
export class AppDataService {
    private _employees = signal<EmployeesInterface[] | null>(null)
    private _employeesActive = signal<EmployeesInterface[] | null>(null)
    private _employeeTypes = signal<EmployeeTypesInterface[] | null>(null)
    private _skills = signal<SkillsInterface[] | null>(null)
    private _leaveTypes = signal<LeaveTypesInterface[] | null>(null)
    private _leaveTypesActive = signal<LeaveTypesInterface[] | null>(null)
    private _ships = signal<ShipsInterface[] | null>(null)
    private _shipTypes = signal<ShipTypesInterface[] | null>(null)
    private _labourSheetCounters = signal<LabourSheetCounterInterface[] | null>(null)
    private _reportPeriods = signal<ReportPeriodInterface[] | null>(null)
    private _employeeLabourCodes = signal<EmployeeLabourCodeInterface[] | null>(null)

    public employees(): Signal<EmployeesInterface[] | null> {
        return this._employees
    }

    public employeesRaw(): EmployeesInterface[] {
        const employees = this._employees()
        if (employees) {
            return employees
        }

        return [] as EmployeesInterface[]
    }

    public setEmployee(value: EmployeesInterface[] | null): void {
        const employees = value?.sort((a, b) => a.firstName.trim().localeCompare(b.firstName.trim())) || []
        employees.forEach((employee) => {
            employee.employeeTypeText = this._employeeTypes()
                ?.find((type) => type.id === employee.employeeTypeId)
                ?.name.toLocaleLowerCase()
        })
        this._employees.set(employees)
    }

    public employeesActive(): Signal<EmployeesInterface[] | null> {
        return this._employeesActive
    }

    public employeesActiveRaw(): EmployeesInterface[] {
        const employees = this._employeesActive()
        if (employees) {
            return employees
        }

        return [] as EmployeesInterface[]
    }

    public setEmployeeActive(value: EmployeesInterface[] | null): void {
        const employList = value?.filter((employee) => !employee.isArchived) || []

        this._employeesActive.set(employList)
    }

    public employeeTypes(): Signal<EmployeeTypesInterface[] | null> {
        return this._employeeTypes
    }

    public employeeTypesRaw(): EmployeeTypesInterface[] {
        const employeeTypes = this._employeeTypes()
        if (employeeTypes) {
            return employeeTypes
        }

        return [] as EmployeeTypesInterface[]
    }

    public setEmployeeTypes(value: EmployeeTypesInterface[] | null): void {
        const employeeTypes = value?.sort((a, b) => a.name.localeCompare(b.name)) || []
        this._employeeTypes.set(employeeTypes)
    }

    public skills(): Signal<SkillsInterface[] | null> {
        return this._skills
    }

    public skillsRaw(): SkillsInterface[] {
        const skills = this._skills()
        if (skills) {
            return skills
        }

        return [] as SkillsInterface[]
    }

    public setSkills(value: SkillsInterface[] | null): void {
        const skills = value
        this._skills.set(skills)
    }

    public leaveTypes(): Signal<LeaveTypesInterface[] | null> {
        return this._leaveTypes
    }
    
    public leaveTypesActive(): Signal<LeaveTypesInterface[] | null> {
        return this._leaveTypesActive
    }

    public leaveTypesRaw(): LeaveTypesInterface[] {
        const leaveTypes = this._leaveTypes()
        if (leaveTypes) {
            return leaveTypes
        }

        return [] as LeaveTypesInterface[]
    }

    public setLeaveTypes(value: LeaveTypesInterface[] | null): void {
        this._leaveTypes.set(value)
        const activeLeaveTypes = value?.filter((type) => !type.isArchived) || []
        this._leaveTypesActive.set(activeLeaveTypes)
    }

    public ships(): Signal<ShipsInterface[] | null> {
        return this._ships
    }

    public shipsRaw(): ShipsInterface[] {
        const ships = this._ships()
        if (ships) {
            return ships
        }

        return [] as ShipsInterface[]
    }

    public setShips(value: ShipsInterface[] | null): void {
        const ships = value?.sort((a, b) => a.name.localeCompare(b.name)) || []
        this._ships.set(ships)
    }

    public shipTypes(): Signal<ShipTypesInterface[] | null> {
        return this._shipTypes
    }

    public shipTypesRaw(): ShipTypesInterface[] {
        const shipTypes = this._shipTypes()
        if (shipTypes) {
            return shipTypes
        }

        return [] as ShipTypesInterface[]
    }

    public setShipTypes(value: ShipTypesInterface[] | null): void {
        const shipTypes = value?.sort((a, b) => a.name.localeCompare(b.name)) || []
        this._shipTypes.set(shipTypes)
    }

    public labourSheetCounters(): Signal<LabourSheetCounterInterface[] | null> {
        return this._labourSheetCounters
    }

    public labourSheetCountersRaw(): LabourSheetCounterInterface[] {
        const labourSheetCounters = this._labourSheetCounters()
        if (labourSheetCounters) {
            return labourSheetCounters
        }

        return [] as LabourSheetCounterInterface[]
    }

    public setLabourSheetCounters(value: LabourSheetCounterInterface[] | null): void {
        this._labourSheetCounters.set(value)
    }

    public reportPeriods(): Signal<ReportPeriodInterface[] | null> {
        return this._reportPeriods
    }

    public reportPeriodsRaw(): ReportPeriodInterface[] {
        const reportPeriods = this._reportPeriods()
        if (reportPeriods) {
            return reportPeriods
        }

        return [] as ReportPeriodInterface[]
    }

    public setReportPeriods(value: ReportPeriodInterface[] | null): void {
        this._reportPeriods.set(value)
    }

    public setAddReportPeriods(value: ReportPeriodInterface[]): void {
        const reportPeriods = this._reportPeriods() || []
        reportPeriods.push(...value)
        this._reportPeriods.set(reportPeriods)
    }

    public employeeLabourCodes(): Signal<EmployeeLabourCodeInterface[] | null> {
        return this._employeeLabourCodes
    }

    public employeeLabourCodesRaw(): EmployeeLabourCodeInterface[] {
        const employeeLabourCodes = this._employeeLabourCodes()
        if (employeeLabourCodes) {
            return employeeLabourCodes
        }

        return [] as EmployeeLabourCodeInterface[]
    }

    public setEmployeeLabourCodes(value: EmployeeLabourCodeInterface[] | null): void {
        const employeeLabourCodes = value?.sort((a, b) => a.name.localeCompare(b.name)) || []
        this._employeeLabourCodes.set(employeeLabourCodes)
    }
}
